import { useMemo } from "react";
import { FiInfo, FiX } from "react-icons/fi";
import ReactSelect, { Props as ReactSelectProps } from "react-select";
import makeAnimated from "react-select/animated";
import { FormLabel } from "../FormLabel";
import ReactTooltip from "react-tooltip";
import FormikFieldError from "../FormikFieldError";
import { createSingleSelectCustomTestIdComponents } from "utils/selectHelper";
import { getReactSelectClassNames, getInputWrapperClasses } from "./inputStyles";
import { isNotNullOrUndefined } from "newui/utils/objectUtil";
import InfoTooltip from "newui/components/InfoTooltip";

const animatedComponents = makeAnimated();

export interface SelectOption {
  label: string;
  value: any;
}

export interface BaseSelectWithLabelProps extends Omit<ReactSelectProps, 'onChange' | 'value'> {
  name?: string;
  className?: string;
  clearAble?: boolean;
  disabled?: boolean;
  errorText?: string;
  guideLine?: string;
  hasError?: boolean;
  height?: string;
  isLoading?: boolean;
  isMulti?: boolean;
  label?: string;
  noLabel?: boolean;
  options: SelectOption[];
  placeholder?: string;
  required?: boolean;
  testId?: string;
  value?: SelectOption | SelectOption[] | null | number;
  width?: string;
  noOptionsMessage?: (obj: { inputValue: string }) => React.ReactNode;
  onChange?: (option: SelectOption | SelectOption[] | null) => void;
  onMenuOpen?: () => void;
}

export const SelectWithLabel: React.FC<BaseSelectWithLabelProps> = ({
  className = "",
  clearAble,
  disabled,
  errorText,
  guideLine,
  hasError = false,
  height,
  isLoading,
  isMulti = false,
  label,
  name,
  noLabel,
  noOptionsMessage,
  onBlur,
  onChange,
  onInputChange,
  onMenuOpen,
  options,
  placeholder,
  required,
  testId,
  value,
  width,
}) => {
  const hasInputError = !!errorText || hasError;

  const mergedComponents = useMemo(() => ({
    ...animatedComponents,
    ...(testId ? createSingleSelectCustomTestIdComponents(testId) : {}),
  }), [testId]);

  const selectedValue = typeof value === 'number' ? options?.find((option) => option.value === value) : value;

  const hasValue = Array.isArray(selectedValue) ? selectedValue.length > 0 : !!(selectedValue && (selectedValue as SelectOption)?.value);

  const handleOnChange = (selected: any) => {
    if (!onChange) {
      return;
    }
    if (isMulti) {
      onChange([...(selected as readonly SelectOption[])]);
    } else {
      onChange(selected as SelectOption | null);
    }
  };

  const menuPortalStyle = (base: any) => ({
    ...base,
    zIndex: 9999,
  });

  return (
    <div
      className={getInputWrapperClasses(hasInputError, false, className)}
      data-testid={testId}
      style={{ width }}
    >
      {label && !noLabel && (
        <FormLabel htmlFor={name} label={label} required={required}>
          {guideLine && (
            <div className="inline-block">
              <InfoTooltip tooltipText={guideLine} />
            </div>
          )}
        </FormLabel>
      )}

      <ReactSelect
        name={name}
        unstyled
        classNames={getReactSelectClassNames(hasInputError, hasValue, disabled, height, isMulti)}
        closeMenuOnSelect={!isMulti}
        components={mergedComponents}
        isMulti={isMulti}
        onChange={handleOnChange}
        onInputChange={onInputChange}
        value={selectedValue}
        options={options}
        isDisabled={disabled}
        placeholder={isLoading ? "Loading..." : placeholder}
        onBlur={onBlur}
        onMenuOpen={onMenuOpen}
        isLoading={isLoading}
        {...(noOptionsMessage ? { noOptionsMessage } : {})}
        menuPortalTarget={document.body}
        styles={{ menuPortal: menuPortalStyle }}
      />

      {clearAble && selectedValue && !isMulti && (
        <>
          <FiX
            className="absolute cursor-pointer"
            data-tip
            data-for={`clear-${name}-tooltip`}
            style={{
              right: "60px",
              width: "20px",
              height: "20px",
              top: "45px",
              color: "gray"
            }}
            onClick={() => handleOnChange(null)}
          />
          <ReactTooltip id={`clear-${name}-tooltip`}>
            Clear Value
          </ReactTooltip>
        </>
      )}

      <FormikFieldError text={errorText} />
    </div>
  );
};
